import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  CircularProgress,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { obterPreInscricao, obterComprovativoPDF } from '../../services/preInscricaoService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ComprovativoPreInscricaoPage = () => {
  const [preInscricao, setPreInscricao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPreInscricao = async () => {
      try {
        const data = await obterPreInscricao(id);
        setPreInscricao(data);
        setLoading(false);
      } catch (err) {
        setError('Não foi possível carregar os detalhes da pré-inscrição.');
        setLoading(false);
      }
    };
    fetchPreInscricao();
  }, [id]);

  useEffect(() => {
    setLogoError(false);
  }, [preInscricao]);

  const handlePrint = () => {
    window.print();
  };

  const handleVoltar = () => {
    navigate('/dashboard');
  };

  const handleDownloadPDF = async () => {
    try {
      const pdfBlob = await obterComprovativoPDF(id);
      const url = window.URL.createObjectURL(new Blob([pdfBlob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comprovativo_pre_inscricao_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch {
      alert('Erro ao gerar comprovativo. Por favor, tente novamente.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, margin: 'auto', mt: 4 }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={handleVoltar} variant="contained" sx={{ mt: 2 }}>
          Voltar para o Dashboard
        </Button>
      </Paper>
    );
  }

  if (!preInscricao) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, margin: 'auto', mt: 4 }}>
        <Typography>Pré-inscrição não encontrada.</Typography>
        <Button onClick={handleVoltar} variant="contained" sx={{ mt: 2 }}>
          Voltar para o Dashboard
        </Button>
      </Paper>
    );
  }

  const dataFormatada = preInscricao.createdAt
    ? format(new Date(preInscricao.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })
    : 'Data não disponível';

  const isColegioInscricao = preInscricao.campanha?.tipoInstituicao === 'Colégio';

  const renderField = (label, value) => (
    <Typography>
      <strong>{label}:</strong> {value !== null && value !== undefined ? value : <span style={{ color: 'red' }}>Não disponível</span>}
    </Typography>
  );

  // Atualização da URL base utilizando a variável de ambiente REACT_APP_API_URL
  const baseUrl = process.env.REACT_APP_API_URL;

  // Verificação de segurança para garantir que a variável de ambiente esteja definida
  if (!baseUrl) {
    console.error('A variável de ambiente REACT_APP_API_URL não está definida');
  }

  const logoPath = preInscricao?.instituicao?.logo;
  const imageUrl = logoPath
    ? (logoPath.startsWith('http')
      ? logoPath
      : `${baseUrl}/uploads/sliders/${logoPath.split('/').pop()}`)
    : null;

  return (
    <Paper sx={{ p: 3, maxWidth: 800, margin: 'auto', mt: 4 }}>
      {imageUrl && !logoError ? (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src={imageUrl}
            alt={`Logo da ${preInscricao.instituicao?.nome || 'Instituição'}`}
            style={{ 
              maxWidth: '200px',
              maxHeight: '100px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto'
            }}
            onError={() => setLogoError(true)}
          />
        </Box>
      ) : (
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>
          {preInscricao.instituicao?.nome || 'Instituição'}
        </Typography>
      )}

      {logoError && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src="/assets/default-logo.png"
            alt="Logotipo Padrão"
            style={{ 
              maxWidth: '200px',
              maxHeight: '100px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Box>
      )}

      <Typography variant="h4" gutterBottom align="center">
        Comprovativo de Pré-Inscrição
      </Typography>

      <Chip
        label={preInscricao.campanha?.tipoInstituicao || 'Não especificado'}
        color="primary"
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Informações do Estudante</Typography>
              {renderField('Nome', preInscricao.estudante?.nome)}
              {renderField('Email', preInscricao.estudante?.email)}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Detalhes da Inscrição</Typography>
              {renderField('Número', preInscricao.id)}
              {renderField('Data', dataFormatada)}
              {renderField('Status', preInscricao.status)}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Detalhes do Curso</Typography>
              {renderField('Campanha', preInscricao.campanha?.nome)}
              {renderField('Instituição', preInscricao.instituicao?.nome)}
              {isColegioInscricao
                ? renderField('Classe', preInscricao.classe?.numero)
                : (
                  <>
                    {renderField('Curso', preInscricao.curso?.nome)}
                    {renderField('Disciplina', preInscricao.disciplina?.nome)}
                  </>
                )
              }
              {renderField('Período', preInscricao.periodo?.nome)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Mensagem de Confirmação</Typography>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {`Olá ${preInscricao.estudante?.nome || 'Estudante'},

Obrigado por se pré-inscrever na campanha "${preInscricao.campanha?.nome || 'N/A'}" da instituição "${preInscricao.instituicao?.nome || 'N/A'}".

${isColegioInscricao
              ? `Classe: ${preInscricao.classe?.numero || 'N/A'}`
              : `Curso: ${preInscricao.curso?.nome || 'N/A'}
Disciplina: ${preInscricao.disciplina?.nome || 'N/A'}`
          }
Período: ${preInscricao.periodo?.nome || 'N/A'}

Sua pré-inscrição foi recebida com sucesso.

Atenciosamente,
Equipe ${preInscricao.instituicao?.nome || 'da Instituição'}`}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" onClick={handlePrint} sx={{ mb: 1 }}>
          Imprimir Comprovativo
        </Button>
        <Button variant="contained" color="secondary" onClick={handleDownloadPDF} sx={{ mb: 1 }}>
          Baixar Comprovativo PDF
        </Button>
        <Button variant="outlined" onClick={handleVoltar} sx={{ mb: 1 }}>
          Voltar para o Dashboard
        </Button>
      </Box>
    </Paper>
  );
};

export default ComprovativoPreInscricaoPage;
